// SPDX-License-Identifier: AGPL-3.0-or-later

import sectionStyles from '@app/features/app/components/dialogs/shared/SettingsSection.module.css';
import styles from '@app/features/ui/accordion/Accordion.module.css';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {CaretDownIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
export interface AccordionProps {
	id: string;
	title: React.ReactNode;
	description?: React.ReactNode;
	defaultExpanded?: boolean;
	expanded?: boolean;
	onExpandedChange?: (expanded: boolean) => void;
	children: React.ReactNode;
	className?: string;
}
export const Accordion: React.FC<AccordionProps> = observer(
	({
		id,
		title,
		description,
		defaultExpanded = false,
		expanded: controlledExpanded,
		onExpandedChange,
		children,
		className,
	}) => {
		const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
		const headerRef = useRef<HTMLButtonElement>(null);
		const scrollContainerRef = useRef<HTMLElement | null>(null);
		const wasAtBottomRef = useRef(false);
		const contentId = `${id}-content`;
		const isControlled = controlledExpanded !== undefined;
		const expanded = isControlled ? controlledExpanded : internalExpanded;
		const handleToggle = useCallback(() => {
			const scrollContainer = headerRef.current?.closest('[data-settings-scroll-container]') as HTMLElement | null;
			wasAtBottomRef.current =
				scrollContainer != null &&
				Math.abs(scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight) < 50;
			scrollContainerRef.current = scrollContainer;
			const newExpanded = !expanded;
			if (isControlled) {
				onExpandedChange?.(newExpanded);
			} else {
				setInternalExpanded(newExpanded);
				onExpandedChange?.(newExpanded);
			}
			if (scrollContainer && !newExpanded) {
				const headerRect = headerRef.current?.getBoundingClientRect();
				const containerRect = scrollContainer?.getBoundingClientRect();
				const offsetFromContainer = headerRect && containerRect ? headerRect.top - containerRect.top : null;
				if (offsetFromContainer !== null) {
					requestAnimationFrame(() => {
						const newHeaderRect = headerRef.current?.getBoundingClientRect();
						const newContainerRect = scrollContainer.getBoundingClientRect();
						if (newHeaderRect && newContainerRect) {
							const newOffset = newHeaderRect.top - newContainerRect.top;
							const delta = newOffset - offsetFromContainer;
							if (Math.abs(delta) > 1) {
								scrollContainer.scrollTop += delta;
							}
						}
					});
				}
			}
		}, [expanded, isControlled, onExpandedChange, id]);
		useEffect(() => {
			if (expanded && scrollContainerRef.current && wasAtBottomRef.current) {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						if (scrollContainerRef.current) {
							scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
						}
					});
				});
			}
			if (!expanded) {
				wasAtBottomRef.current = false;
				scrollContainerRef.current = null;
			}
		}, [expanded]);
		return (
			<div className={clsx(styles.accordion, className)} id={id}>
				<FocusRing offset={-2}>
					<button
						ref={headerRef}
						type="button"
						className={styles.header}
						onClick={handleToggle}
						aria-expanded={expanded}
						aria-controls={contentId}
					>
						<div className={styles.headerContent}>
							<span className={sectionStyles.sectionTitle}>{title}</span>
							{description ? <span className={sectionStyles.sectionDescription}>{description}</span> : null}
						</div>
						<CaretDownIcon className={clsx(styles.caret, expanded && styles.caretExpanded)} size={20} weight="bold" />
					</button>
				</FocusRing>
				{expanded ? (
					<div id={contentId} className={styles.contentWrapper}>
						<div className={styles.content}>{children}</div>
					</div>
				) : null}
			</div>
		);
	},
);
